import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { Filter, ExternalLink } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Pagination } from '@/components/Pagination'
import { useActivityLogs } from '@/hooks/useActivity'
import { useUsers } from '@/hooks/useUsers'
import { ActivityLogFilterParams, ActivityAction, EntityType } from '@/types/api'

const actionLabels: Record<ActivityAction, string> = {
  [ActivityAction.CREATE]: 'Created',
  [ActivityAction.UPDATE]: 'Updated',
  [ActivityAction.DELETE]: 'Deleted',
  [ActivityAction.TRANSITION]: 'Transitioned',
}

const actionColors: Record<ActivityAction, string> = {
  [ActivityAction.CREATE]: 'success',
  [ActivityAction.UPDATE]: 'info',
  [ActivityAction.DELETE]: 'destructive',
  [ActivityAction.TRANSITION]: 'warning',
}

const entityLabels: Record<EntityType, string> = {
  [EntityType.LEAD]: 'Lead',
  [EntityType.CUSTOMER]: 'Customer',
  [EntityType.USER]: 'User',
  [EntityType.PROJECT]: 'Project',
}

export function ActivityPage() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<ActivityLogFilterParams>({
    limit: 25,
    offset: 0,
    sort_by: 'created_at',
    sort_order: 'desc',
  })

  const { data: logs, isLoading } = useActivityLogs(filters)
  const { data: users } = useUsers()

  const handleFilterChange = (
    key: keyof ActivityLogFilterParams,
    value: string
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      offset: 0,
    }))
  }

  const handleViewEntity = (entityType: EntityType, entityId: string) => {
    switch (entityType) {
      case EntityType.LEAD:
        navigate(`/leads/${entityId}`)
        break
      case EntityType.CUSTOMER:
        navigate(`/customers/${entityId}`)
        break
      case EntityType.USER:
        navigate(`/settings`)
        break
      case EntityType.PROJECT:
        // Projects don't have standalone pages; no-op
        break
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Activity Log</h1>
          <p className="text-muted-foreground">
            View all system activity and changes
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <Select
              value={filters.user_id || ''}
              onValueChange={(v) => handleFilterChange('user_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users?.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.entity_type || ''}
              onValueChange={(v) => handleFilterChange('entity_type', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Entity Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entity Types</SelectItem>
                <SelectItem value={EntityType.LEAD}>Lead</SelectItem>
                <SelectItem value={EntityType.CUSTOMER}>Customer</SelectItem>
                <SelectItem value={EntityType.USER}>User</SelectItem>
                <SelectItem value={EntityType.PROJECT}>Project</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.action || ''}
              onValueChange={(v) => handleFilterChange('action', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value={ActivityAction.CREATE}>Create</SelectItem>
                <SelectItem value={ActivityAction.UPDATE}>Update</SelectItem>
                <SelectItem value={ActivityAction.DELETE}>Delete</SelectItem>
                <SelectItem value={ActivityAction.TRANSITION}>
                  Transition
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead className="hidden md:table-cell">User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead className="hidden md:table-cell">Entity</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="hidden md:table-cell">View</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs?.items.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {format(new Date(log.created_at), 'PPp')}
                      </TableCell>
                      <TableCell className="hidden md:table-cell font-medium">
                        {log.username || 'System'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            actionColors[log.action] as
                              | 'success'
                              | 'info'
                              | 'destructive'
                              | 'warning'
                          }
                        >
                          {actionLabels[log.action]}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="capitalize">
                          {entityLabels[log.entity_type]}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-md truncate">
                        {log.description || '-'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {log.action !== ActivityAction.DELETE && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleViewEntity(log.entity_type, log.entity_id)
                            }
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!logs?.items || logs.items.length === 0) && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-muted-foreground"
                      >
                        No activity logs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Pagination
                total={logs?.total || 0}
                limit={filters.limit || 25}
                offset={filters.offset || 0}
                onPageChange={(offset) =>
                  setFilters((prev) => ({ ...prev, offset }))
                }
                onLimitChange={(limit) =>
                  setFilters((prev) => ({ ...prev, limit, offset: 0 }))
                }
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
